import React from "react";
import PrefixedLink from "components/PrefixedLink";
import { moduleType, pathModuleType } from "config/urlConfig";
import { useParams } from "react-router-dom";

const isExternal = (to) => /^https?:\/\//i.test(to);

const SmartLink = ({ to, className = "", children, onClick, ...rest }) => {
	const params = useParams();
	const resolvedTo = typeof to === "string" && to.startsWith("#") ? `/${to}` : to;
	
	// Check if we're on a module subdomain or using pathModuleType
	const isModuleSubdomain = Boolean(moduleType);
	const isPathModuleMode = Boolean(pathModuleType);
	
	// Use the active module config - either from subdomain or dev config
	const activeModule = moduleType || pathModuleType;
	
	if (resolvedTo.startsWith("mailto:") || resolvedTo.startsWith("tel:")) {
		return (
			<a 
				href={resolvedTo} 
				className={className} 
				onClick={onClick} 
				{...rest}>
				{children}
			</a>
		);
	} else if (isExternal(resolvedTo)) {
		return (
			<a 
				href={resolvedTo} 
				className={className} 
				target="_blank" 
				rel="noopener noreferrer" 
				onClick={onClick} 
				{...rest}>
				{children}
			</a>
		);
	} else {
		// For internal links, modify path based on context
		let adjustedTo = resolvedTo;
		
		// If we're on a module subdomain or using pathModuleType in DEV mode, adjust URLs
		if ((isModuleSubdomain || isPathModuleMode) && activeModule?.module) {
			// For links starting with a slash, adjust them for module context
			if (typeof adjustedTo === 'string' && adjustedTo.startsWith('/')) {
				// Extract organization from URL or params
				const orgUrl = params.org_url || window.location.pathname.split('/')[1];
				
				if (orgUrl) {
					// Aggressive URL cleanup for module subdomains
					
					// Case 1: URL contains both org and module path: /orgUrl/modulePath/uniqueUrl
					const fullModulePathPattern = new RegExp(`^\\/${orgUrl}\\/${activeModule.module}\\/(.+)$`);
					if (adjustedTo.match(fullModulePathPattern)) {
						adjustedTo = adjustedTo.replace(fullModulePathPattern, `/${orgUrl}/$1`);
					}
					// Case 2: URL contains just module path without org: /modulePath/uniqueUrl
					else if (adjustedTo.startsWith(`/${activeModule.module}/`)) {
						const modulePrefixPattern = new RegExp(`^\\/${activeModule.module}\\/(.+)$`);
						adjustedTo = `/${orgUrl}/${adjustedTo.replace(modulePrefixPattern, '$1')}`;
					}
					// Case 3: URL already has org prefix but might have extra segments
					else if (adjustedTo.startsWith(`/${orgUrl}/`)) {
						const pathSegments = adjustedTo.split('/').filter(Boolean);
						// Check if this might be a module path we need to clean
						if (pathSegments.length > 2 && pathSegments[1] === activeModule.module) {
							// Take only the org and the last segment (uniqueUrl)
							adjustedTo = `/${pathSegments[0]}/${pathSegments[pathSegments.length - 1]}`;
						}
					}
				}
			}
		}
		
		return (
			<PrefixedLink 
				to={adjustedTo} 
				className={className} 
				onClick={onClick} 
				isModuleSubdomain={isModuleSubdomain || isPathModuleMode} 
				moduleType={activeModule}
				{...rest}>
				{children}
			</PrefixedLink>
		);
	}
};

export default SmartLink;
